import { lightBlue } from '@mui/material/colors'
import { alpha } from '@mui/material/styles'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'

// Shared so other map segmented controls (e.g. the route A | All | route B toggle) match this style.
export const getMapToggleGroupSx = theme => {
  const isDark = theme.palette.mode === 'dark'
  const accent = isDark ? lightBlue[400] : theme.palette.primary.main
  const { glass } = theme.palette
  return {
    bgcolor: glass?.surface ?? alpha(theme.palette.background.paper, 0.92),
    // Light maps render the raw divider as a harsh black outline — drop the
    // border there and lean on the shadow; dark keeps its border.
    border: isDark ? `1px solid ${glass?.border ?? alpha(theme.palette.divider, 0.6)}` : 'none',
    borderRadius: '4px',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    boxShadow: glass?.shadow ?? theme.shadows[2],
    '& .MuiToggleButton-root': {
      typography: 'overline',
      color: theme.palette.text.secondary,
      border: 'none',
      px: '10px',
      py: '6px',
      letterSpacing: '0.3px',
      textTransform: 'none',
      gap: '6px',
      '&:hover': {
        bgcolor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
      '&.Mui-selected': {
        bgcolor: alpha(accent, isDark ? 0.16 : 0.26),
        color: accent,
        '&:hover': { bgcolor: alpha(accent, isDark ? 0.22 : 0.34) },
      },
    },
    '& .MuiSvgIcon-root': { fontSize: theme.typography.h5.fontSize },
  }
}

const renderItem = ({ value, label, icon, tooltip }) => {
  const button = (
    <ToggleButton key={value} value={value}>
      {icon}
      {label}
    </ToggleButton>
  )
  return tooltip ? (
    <Tooltip key={value} arrow title={tooltip}>
      {button}
    </Tooltip>
  ) : (
    button
  )
}

const MapLayerToggles = ({ value, onChange, items, ariaLabel = 'Map layers' }) => {
  const handleChange = useCallback((_event, next) => onChange(next ?? []), [onChange])
  return (
    <ToggleButtonGroup aria-label={ariaLabel} size="small" sx={getMapToggleGroupSx} value={value} onChange={handleChange}>
      {items.map(renderItem)}
    </ToggleButtonGroup>
  )
}

MapLayerToggles.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      tooltip: PropTypes.string,
    })
  ).isRequired,
  ariaLabel: PropTypes.string,
}

export default MapLayerToggles
